import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Badge,
  Button,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Menu as MenuIcon,
  Analytics as AnalyticsIcon,
  Tune as OptimizeIcon,
  Settings as SetupIcon,
  Group as AffiliatesIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  Edit as CreateBlogIcon,
  Assessment as ReportingIcon,
  Notifications as NotificationsIcon,
  ExitToApp as ExitToAppIcon,
  Person as PersonIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
// import ThemeToggle from './ThemeToggle'; // Hidden theme toggle

const drawerWidth = 240; // Reduced from 280px to 220px for more compact sidebar

const DashboardLayout = ({ children, title, actions, maxWidth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const menuSections = [
    {
      title: '',
      items: [
        { text: 'Reporting', icon: <ReportingIcon />, path: '/reporting' },
        { text: 'Optimize', icon: <OptimizeIcon />, path: '/optimize' },
        { text: 'Set Ups', icon: <SetupIcon />, path: '/setups' },
        { text: 'Affiliates', icon: <AffiliatesIcon />, path: '/affiliate-partners', adminOnly: true },
        { text: 'Brands', icon: <BusinessIcon />, path: '/brands' },
        { text: 'Create Blog', icon: <CreateBlogIcon />, path: '/blog' },
        { text: 'User Accounts', icon: <GroupIcon />, path: '/affiliates', adminOnly: true },
      ]
    }
  ];

  const filteredMenuSections = menuSections.map(section => ({
    ...section,
    items: section.items.filter(item => {
      // For affiliate users, hide admin-only items and show affiliate-specific items
      if (user?.role === 'affiliate') {
        return !item.adminOnly;
      }
      // For admin users, show all items
      if (user?.role === 'admin') {
        return true;
      }
      // For other users, hide admin-only items
      return !item.adminOnly;
    })
  })).filter(section => section.items.length > 0);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img 
          src="/esx_logo.jpg" 
          alt="ESX Logo" 
          style={{
            height: '50px',
            width: '50px',
            objectFit: 'contain'
          }}
        />
      </Box>
      
      <Box sx={{ flex: 1, p: 2 }}>
        {filteredMenuSections.map((section, sectionIndex) => (
          <Box key={section.title} sx={{ mb: 3 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                px: 2, 
                py: 1, 
                color: 'text.secondary', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontSize: '0.75rem'
              }}
            >
              {section.title}
            </Typography>
            <List sx={{ pt: 1 }}>
              {section.items.map((item) => {
                const isActive = location.pathname === item.path || 
                  (item.path === '/dashboard' && location.pathname === '/');
                
                return (
                  <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      selected={isActive}
                      onClick={() => {
                        console.log('Navigating to:', item.path, 'for item:', item.text);
                        navigate(item.path);
                      }}
                      sx={{
                        borderRadius: 2,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(59, 130, 246, 0.15)',
                          '& .MuiListItemIcon-root': {
                            color: 'primary.main',
                          },
                          '& .MuiListItemText-primary': {
                            color: 'primary.main',
                            fontWeight: 600,
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: isActive ? 600 : 500,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            {sectionIndex < filteredMenuSections.length - 1 && (
              <Divider sx={{ mx: 2, my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        {/* Theme Toggle Section - Hidden */}
        {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pb: 2 }}>
          <ThemeToggle />
        </Box> */}
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }} onClick={handleProfileMenuOpen}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              {user?.email?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, truncate: true }}>
                {user?.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.role}
              </Typography>
            </Box>
          </Box>
          <IconButton color="inherit" onClick={handleSignOut} title="Sign Out">
            <ExitToAppIcon />
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorEl}
          id="profile-menu"
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleProfileClick}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </MenuItem>
          <MenuItem onClick={handleSignOut}>
            <ListItemIcon>
              <ExitToAppIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Sign out" />
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        {/* Clean title bar */}
        <Box sx={{ maxWidth: maxWidth || 'xl', mx: 'auto', px: 3, pt: 3, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { sm: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {title}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton color="inherit">
                <Badge badgeContent={4} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              {actions}
            </Box>
          </Box>
        </Box>

        {/* Page content */}
        <Box>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout; 